import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import * as Api from "@/api";
import ActivityBadge from "@/app/domain/dashboard/components/ActivityBadge";
import Avatar from "@/design/components/Avatar";
import useAccessibleColors from "@/utils/hooks/useAccessibleColors";
import Valkyrie, { viLocationPinDot, viLock, viLockOpen, viSpinnerThird, viTrashCan } from "@sippy-platform/valkyrie";

import {
  Button,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";

function MemberItem({ member, queue, queueDispatch }) {
  const theme = useTheme();
  const colors = useAccessibleColors();
  const matchesSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  const [pending, setPending] = useState(false);
  const [lockPending, setLockPending] = useState(false);

  const queueMembersStore = useSelector((state) => state.domain.queueMembers);

  const itemMember = useMemo(() => {
    return queueMembersStore.list.find((_member) => member.exten === _member.attributes.exten);
  }, [member.exten, queueMembersStore.list]);

  function handleMemberLeave() {
    setPending(true);

    Promise.resolve(Api.deleteQueueMember(queue, member))
      .then(() => queueDispatch({ type: "removeMember", member }))
      .finally(() => setPending(false));
  }

  function handleMemberToggleLock() {
    setLockPending(true);

    Promise.resolve(Api.putQueueMember(queue, member, member.lock === 1 ? "unlock" : "lock"))
      .then(() => queueDispatch({ type: "toggleMemberLock", member, status: member.lock }))
      .finally(() => setLockPending(false));
  }

  return (
    <ListItem
      key={member.exten}
      sx={(theme) => ({
        "&:nth-of-type(odd)": {
          backgroundColor: "#f8f8f8",
          ...theme.applyStyles("dark", {
            backgroundColor: "#28282d",
          }),
        },
        borderRadius: 1,
        "&:hover": {
          backgroundColor: "#efefef",
          ...theme.applyStyles("dark", {
            backgroundColor: "#323237",
          }),
        },
      })}
      secondaryAction={
        <Stack
          direction="row"
          sx={{
            gap: 0.25,
          }}
        >
          {member.lock ? (
            <>
              {matchesSmUp ? (
                <Button
                  endIcon={<Valkyrie icon={lockPending ? viSpinnerThird : viLock} spin={lockPending} />}
                  sx={{ px: 1.25 }}
                  onClick={handleMemberToggleLock}
                  disabled={pending || lockPending}
                  color="error"
                >
                  Locked
                </Button>
              ) : (
                <IconButton onClick={handleMemberToggleLock} disabled={pending || lockPending} color="error">
                  <Valkyrie icon={lockPending ? viSpinnerThird : viLock} spin={lockPending} />
                </IconButton>
              )}
            </>
          ) : (
            <IconButton onClick={handleMemberToggleLock} disabled={pending || lockPending}>
              <Valkyrie icon={lockPending ? viSpinnerThird : viLockOpen} spin={lockPending} />
            </IconButton>
          )}
          <IconButton onClick={handleMemberLeave} disabled={pending}>
            <Valkyrie icon={pending ? viSpinnerThird : viTrashCan} spin={pending} />
          </IconButton>
        </Stack>
      }
    >
      <ListItemAvatar>
        <ActivityBadge color={member.paused === 1 ? colors.red : colors.green} size="small">
          <Avatar text={itemMember.attributes.name} icon={itemMember.type === "user" ? undefined : viLocationPinDot} />
        </ActivityBadge>
      </ListItemAvatar>
      <ListItemText primary={itemMember.attributes.name} secondary={member.exten} />
    </ListItem>
  );
}

MemberItem.propTypes = {
  member: PropTypes.object,
  queue: PropTypes.object,
  queueDispatch: PropTypes.func,
};

export default MemberItem;
